import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Chip,
  alpha,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Build as BuildIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import BillWritableTable from "../../../components/BillWritableTable";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import { getImportItemById } from "../../../services/Import";

/**
 * Dialog for creating reconstruction batch for damaged items
 * Similar to AddSubOrder flow for selecting input materials
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - checkRecord: The check record with damaged items (should include reconstructions array)
 * - onReconstruct: function(reconstructionData) - callback
 */
function ReconstructionDialog({ open, onClose, checkRecord, onReconstruct }) {
  const theme = useTheme();

  // Form state
  const [reconstructedQty, setReconstructedQty] = useState("");
  const [reconstructedBy, setReconstructedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // Input items for reconstruction (following AddSubOrder pattern)
  const [inputItems, setInputItems] = useState([]);
  const [openItemSelector, setOpenItemSelector] = useState(false);
  const [selectedImportIds, setSelectedImportIds] = useState([]);
  const [rows, setRows] = useState([]);

  // ✅ NEW: State to track reconstruction quantities
  const [totalDamaged, setTotalDamaged] = useState(0);
  const [totalReconstructed, setTotalReconstructed] = useState(0);
  const [availableQty, setAvailableQty] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Columns for input items table (matching AddSubOrder pattern)
  const inputColumns = [
    { id: "item", label: "Item", minWidth: 150, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    {
      id: "qty",
      label: "Qty",
      minWidth: 80,
      editable: true,
      type: "number",
      isDecimal: true,
    },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: true },
    { id: "unitType", label: "Item Unit", minWidth: 100, editable: false },
    {
      id: "itemUnitsPerOrderUnit",
      label: "Item Units Amount",
      minWidth: 120,
      editable: true,
      type: "number",
      isDecimal: true,
    },
  ];

  useEffect(() => {
    if (open && checkRecord) {
      // ✅ UPDATED: Calculate available quantity for reconstruction
      const damaged = checkRecord.damagedQty || 0;

      // Get existing reconstructions from checkRecord if available
      // These should be passed from the backend when fetching damaged batches
      const reconstructions = checkRecord.reconstructions || [];

      // Calculate total already in reconstruction
      const reconstructed = reconstructions.reduce(
        (sum, r) => sum + (r.reconstructedQty || 0),
        0
      );

      // Calculate remaining available quantity
      const remaining = damaged - reconstructed;

      // Update state
      setTotalDamaged(damaged);
      setTotalReconstructed(reconstructed);
      setAvailableQty(remaining);

      // ✅ Set default reconstructed qty to REMAINING amount (not total damaged)
      setReconstructedQty(remaining > 0 ? remaining : "");

      // Reset other fields
      setReconstructedBy("");
      setNotes("");
      setInputItems([]);
      setSelectedImportIds([]);
      setError("");

      console.log("[RECONSTRUCTION DIALOG] Calculation:", {
        totalDamaged: damaged,
        alreadyReconstructed: reconstructed,
        availableForReconstruction: remaining,
      });
    }
  }, [open, checkRecord]);

  // Open item selector (following AddSubOrder pattern)
  const handleOpenItemSelector = () => {
    setOpenItemSelector(true);
  };

  // Close item selector and fetch selected items
  const handleCloseItemSelector = async () => {
    setOpenItemSelector(false);
    console.log("Selected import IDs:", selectedImportIds);

    // Fetch details for newly selected items
    if (selectedImportIds.length > 0) {
      const newItems = [];

      for (const importId of selectedImportIds) {
        // Check if item already exists
        const exists = inputItems.some((item) => item.importId === importId);
        if (exists) continue;

        try {
          const importItem = await getImportItemById(importId);
          console.log("Fetched import item:", importItem);

          if (importItem && importItem !== "Something went wrong...") {
            const newRow = createDataForInputTable(
              importItem.itemName + " (" + importItem.product_code + ")",
              importItem.brand,
              1, // Default quantity
              importItem.unitType,
              importItem.unitType,
              1.0, // Default conversion
              importItem.importId,
              importItem.qty
            );
            newItems.push(newRow);
          }
        } catch (error) {
          console.error(`Error fetching import item ${importId}:`, error);
        }
      }

      setInputItems([...inputItems, ...newItems]);
      setSelectedImportIds([]); // Reset selection for next time
    }
  };

  // Create table row data (following AddSubOrder pattern)
  function createDataForInputTable(
    item,
    brand,
    qty,
    orderUnit,
    unitType,
    itemUnitsPerOrderUnit,
    importId,
    existingQty
  ) {
    return {
      item,
      brand,
      qty,
      orderUnit,
      unitType,
      itemUnitsPerOrderUnit,
      importId,
      existingQty,
    };
  }

  const handleReconstruct = () => {
    // Validation
    if (!reconstructedQty || parseFloat(reconstructedQty) <= 0) {
      setError("Reconstructed quantity must be greater than 0");
      return;
    }

    // ✅ UPDATED: Validate against available quantity (not total damaged)
    if (parseFloat(reconstructedQty) > availableQty) {
      setError(
        `Cannot reconstruct more than ${availableQty} units. ` +
          `(Total damaged: ${totalDamaged}, Already in reconstruction: ${totalReconstructed})`
      );
      return;
    }

    if (!reconstructedBy.trim()) {
      setError("Please enter who is reconstructing");
      return;
    }

    // Prepare input items data (matching backend CreateReconstructionBody)
    const inputItemsData = inputItems.map((item) => ({
      importId: item.importId,
      qty: parseFloat(item.qty),
      itemUnit: item.orderUnit,
      prodUnitsForItemUnit: parseFloat(item.itemUnitsPerOrderUnit),
      notes: "",
    }));

    console.log("Reconstruction data:", {
      checkId: checkRecord.id,
      reconstructedQty: parseFloat(reconstructedQty),
      reconstructedBy: reconstructedBy.trim(),
      notes: notes.trim(),
      inputItems: inputItemsData,
    });

    // Call callback with reconstruction data
    onReconstruct({
      checkId: checkRecord.id,
      reconstructedQty: parseFloat(reconstructedQty),
      reconstructedBy: reconstructedBy.trim(),
      notes: notes.trim(),
      inputItems: inputItemsData,
    });

    // Close and reset
    handleClose();
  };

  const handleClose = () => {
    setReconstructedQty("");
    setReconstructedBy("");
    setNotes("");
    setInputItems([]);
    setSelectedImportIds([]);
    setError("");
    onClose();
  };

  if (!checkRecord) return null;

  const lineItem = checkRecord.receive?.subOrderLineItem;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BuildIcon color="primary" />
              <Typography variant="h6">Create Reconstruction Batch</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Damaged Item Information */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle2"
              color="error"
              gutterBottom
              fontWeight="600"
            >
              Damaged Items
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Typography variant="body2" color="text.secondary">
                  Item
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {lineItem?.imports?.itemName || "Unknown Item"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {lineItem?.imports?.brand || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Damaged
                </Typography>
                <Typography variant="h6" color="error.main" fontWeight="700">
                  {totalDamaged}
                </Typography>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="body2" color="text.secondary">
                  In Reconstruction
                </Typography>
                <Typography variant="h6" color="warning.main" fontWeight="700">
                  {totalReconstructed}
                </Typography>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Available</strong>
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight="700">
                  {availableQty}
                </Typography>
              </Grid>
              <Grid item xs={12} md={1}>
                <Typography variant="body2" color="text.secondary">
                  Batch
                </Typography>
                <Typography variant="body1">#{checkRecord.id}</Typography>
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* ✅ Show warning if no quantity available */}
          {availableQty <= 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              All damaged items from this batch are already in reconstruction.
              Please complete or cancel existing reconstructions before creating
              a new one.
            </Alert>
          )}

          {/* Reconstruction Form */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Quantity to Reconstruct"
                value={reconstructedQty}
                onChange={(e) => setReconstructedQty(e.target.value)}
                onWheel={(e) => e.target.blur()}
                disabled={availableQty <= 0}
                InputProps={{
                  inputProps: { min: 0, max: availableQty, step: 0.01 },
                }}
                helperText={
                  availableQty > 0
                    ? `Maximum: ${availableQty} units available`
                    : "No units available for reconstruction"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Reconstructed By"
                value={reconstructedBy}
                onChange={(e) => setReconstructedBy(e.target.value)}
                placeholder="Enter person's name"
                disabled={availableQty <= 0}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reconstruction notes..."
                disabled={availableQty <= 0}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Input Materials Section (following AddSubOrder pattern) */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Required Input Materials (Optional)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenItemSelector}
                disabled={availableQty <= 0}
              >
                Add Materials
              </Button>
            </Box>

            {inputItems.length === 0 ? (
              <Alert severity="info">
                No input materials added. This will be manual reconstruction
                only.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Input Materials
                </Typography>
                <BillWritableTable
                  columns={inputColumns}
                  rows={inputItems}
                  setRows={setInputItems}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  tablePagin={false}
                />
              </>
            )}
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Note:</strong> Input materials will be reserved from
              inventory for reconstruction.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleReconstruct}
            variant="contained"
            color="primary"
            startIcon={<BuildIcon />}
            disabled={availableQty <= 0}
          >
            Start Reconstruction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Selector Popup (following AddSubOrder pattern) */}
      {openItemSelector && (
        <ItemSelectingPopup
          setOpenTable={setOpenItemSelector}
          openTable={openItemSelector}
          handleCloseTable={handleCloseItemSelector}
          setSelectedItems={setSelectedImportIds}
          selectedItems={selectedImportIds}
          setRows={setRows}
          rows={rows}
          isOneChoise={false}
        />
      )}
    </>
  );
}

export default ReconstructionDialog;
